/**
 * Ops Checklists API Service
 *
 * Manual operational checklists with RAG status tracking and 2nd checker workflow.
 */

import { get, post, patch, del, apiRequest } from "./client";

// ============================================================================
// Types
// ============================================================================

export type StepStatus = "green" | "amber" | "red" | "pending";
export type RunStatus = "in_progress" | "awaiting_review" | "approved" | "rejected";
export type Weekday = "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | "sunday";

/**
 * Team members available for selection
 */
export const OPS_TEAM_MEMBERS = [
  "Sam",
  "Vasi",
  "Sid",
  "Lachlan",
  "Timo",
  "Jude",
  "Andy",
  "Remi",
] as const;

export type TeamMember = (typeof OPS_TEAM_MEMBERS)[number];

/**
 * A single step in a checklist definition
 */
export interface ChecklistStep {
  step_id: string;
  order: number;
  title: string;
  instructions: string;
}

export type AIReviewRating = "green" | "amber" | "red";

/**
 * A user's response to a step during a run
 */
export type DraftRag = "green" | "amber" | "red";

export interface StepResponse {
  step_id: string;
  status: StepStatus;
  notes: string;
  assigned_to?: string;
  flagged_confirmed: boolean;
  completed_at: string;
  reviewer_comment?: string;
  screenshot_url?: string;
  /** When status is `pending`, which RAG option was selected in the UI */
  draft_rag?: DraftRag;
  // Timmy AI review fields
  ai_review_rating?: AIReviewRating;
  ai_review_feedback?: string;
  ai_review_ignored?: boolean;
  ai_reviewed_at?: string;
  /** Second checker: true = accepted, false = rejected, absent/null = not set */
  reviewer_step_accepted?: boolean | null;
}

/** Default Timmy AI behavior when completing steps (configure per checklist). */
export type TimmyReviewDefault = "required" | "skip";

/**
 * A checklist definition (template)
 */
export interface Checklist {
  checklist_id: string;
  name: string;
  description?: string;
  /** Short reminders shown on the dashboard and during runs */
  bullet_points?: string[];
  steps: ChecklistStep[];
  schedule_days_of_week: Weekday[];
  schedule_days_of_month: number[];
  ttl_days: number;
  default_assignee?: string;
  deadline?: string;
  /** Run Timmy before finalize vs skip by default (runners can override per step). */
  timmy_review_default?: TimmyReviewDefault;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * A single execution of a checklist
 */
export interface ChecklistRun {
  run_id: string;
  checklist_id: string;
  checklist_name: string;
  date: string;
  status: RunStatus;
  primary_user?: string;
  started_at?: string;
  completed_at?: string;
  steps: StepResponse[];
  second_checker?: string;
  reviewed_at?: string;
  review_notes?: string;
  rejection_count: number;
  rejection_notes: string[];
  created_at: string;
  updated_at: string;
}

/**
 * Summary of a checklist for today's dashboard
 */
export interface TodayChecklistSummary {
  checklist_id: string;
  checklist_name: string;
  description?: string;
  bullet_points?: string[];
  total_steps: number;
  default_assignee?: string;
  deadline?: string;
  run?: ChecklistRun;
  completed_steps: number;
  has_red_steps: boolean;
  has_amber_steps: boolean;
}

/**
 * Today's dashboard response
 */
export interface TodayDashboardResponse {
  date: string;
  checklists: TodayChecklistSummary[];
  stats: {
    total: number;
    not_started: number;
    in_progress: number;
    awaiting_review: number;
    approved: number;
    rejected: number;
  };
}

/**
 * History response with pagination
 */
export interface HistoryResponse {
  runs: ChecklistRun[];
  total: number;
  limit: number;
  skip: number;
}

/**
 * A single amber/red issue from a checklist run
 */
export interface IssueItem {
  run_id: string;
  checklist_id: string;
  checklist_name: string;
  date: string;
  step_id: string;
  step_order: number;
  step_title: string;
  status: "amber" | "red";
  notes: string;
  assigned_to?: string;
  flagged_confirmed: boolean;
  flagged_at: string;
  run_status: RunStatus;
  primary_user?: string;
}

/**
 * Response containing all amber/red issues
 */
export interface IssuesResponse {
  issues: IssueItem[];
  stats: {
    total: number;
    amber: number;
    red: number;
    resolved: number;
    unresolved: number;
  };
}

// ============================================================================
// Request Types
// ============================================================================

export interface StartRunRequest {
  user_name: string;
}

export interface CompleteStepRequest {
  status: StepStatus;
  notes: string;
  assigned_to?: string;
  flagged_confirmed?: boolean;
  screenshot_url?: string;
  // Timmy AI review data
  ai_review_rating?: AIReviewRating;
  ai_review_feedback?: string;
  ai_review_ignored?: boolean;
}

export interface SaveStepProgressRequest {
  draft_rag?: DraftRag;
  notes?: string;
  assigned_to?: string;
  flagged_confirmed?: boolean;
  screenshot_url?: string;
}

export interface SubmitForReviewRequest {
  second_checker: string;
}

export interface ReviewRunRequest {
  approved: boolean;
  notes?: string;
}

export interface AddStepCommentRequest {
  /** Non-empty comment text (optional if reviewer_step_accepted is set) */
  comment?: string;
  /** When set, records second-checker accept (true) or reject (false) for this step */
  reviewer_step_accepted?: boolean;
}

export interface AIReviewRequest {
  step_title: string;
  step_instructions: string;
  user_status: StepStatus;
  user_notes: string;
  screenshot_url?: string;
}

export interface AIReviewResponse {
  rating: AIReviewRating;
  feedback: string;
  reviewed_at: string;
}

export interface CreateChecklistRequest {
  name: string;
  description?: string;
  bullet_points?: string[];
  steps: ChecklistStep[];
  schedule_days_of_week?: Weekday[];
  schedule_days_of_month?: number[];
  ttl_days?: number;
  default_assignee?: string;
  deadline?: string | null;
  timmy_review_default?: TimmyReviewDefault;
}

export interface UpdateChecklistRequest {
  name?: string;
  description?: string;
  bullet_points?: string[];
  steps?: ChecklistStep[];
  schedule_days_of_week?: Weekday[];
  schedule_days_of_month?: number[];
  ttl_days?: number;
  default_assignee?: string;
  deadline?: string | null;
  is_active?: boolean;
  timmy_review_default?: TimmyReviewDefault;
}

export interface HistoryFilters {
  checklist_id?: string;
  user_name?: string;
  status?: RunStatus;
  date_from?: string;
  date_to?: string;
  limit?: number;
  skip?: number;
}

export interface IssuesFilters {
  date_from?: string;
  date_to?: string;
  assigned_to?: string;
  status?: "amber" | "red";
}

// ============================================================================
// API Functions
// ============================================================================

const BASE_PATH = "/admin/ops-checklists";

/**
 * Get available team members
 */
export async function getTeamMembers(): Promise<string[]> {
  const response = await get<{ members: string[] }>(`${BASE_PATH}/team-members`);
  return response.members;
}

/**
 * Get today's dashboard
 */
export async function getTodayDashboard(
  date?: string
): Promise<TodayDashboardResponse> {
  const params = date ? `?date=${date}` : "";
  return get<TodayDashboardResponse>(`${BASE_PATH}/today${params}`);
}

/**
 * Start a new run for a checklist
 */
export async function startRun(
  checklistId: string,
  request: StartRunRequest,
  date?: string
): Promise<ChecklistRun> {
  const params = date ? `?date=${date}` : "";
  return post<ChecklistRun>(
    `${BASE_PATH}/checklists/${checklistId}/runs${params}`,
    request
  );
}

/**
 * Get a run by ID
 */
export async function getRun(runId: string): Promise<ChecklistRun> {
  return get<ChecklistRun>(`${BASE_PATH}/runs/${encodeURIComponent(runId)}`);
}

/**
 * Complete a step in a run
 */
export async function completeStep(
  runId: string,
  stepId: string,
  request: CompleteStepRequest
): Promise<ChecklistRun> {
  const rid = encodeURIComponent(runId);
  const sid = encodeURIComponent(stepId);
  return patch<ChecklistRun>(
    `${BASE_PATH}/runs/${rid}/steps/${sid}`,
    request
  );
}

/**
 * Save in-progress answers for a step without completing it (does not submit for review).
 */
export async function saveStepProgress(
  runId: string,
  stepId: string,
  request: SaveStepProgressRequest
): Promise<ChecklistRun> {
  const rid = encodeURIComponent(runId);
  const sid = encodeURIComponent(stepId);
  return patch<ChecklistRun>(
    `${BASE_PATH}/runs/${rid}/steps/${sid}/progress`,
    request
  );
}

/**
 * Submit a run for second checker review
 */
export async function submitForReview(
  runId: string,
  request: SubmitForReviewRequest
): Promise<ChecklistRun> {
  return post<ChecklistRun>(`${BASE_PATH}/runs/${runId}/submit`, request);
}

/**
 * Second checker approves or rejects a run
 */
export async function reviewRun(
  runId: string,
  reviewer: string,
  request: ReviewRunRequest
): Promise<ChecklistRun> {
  return post<ChecklistRun>(
    `${BASE_PATH}/runs/${runId}/review?reviewer=${encodeURIComponent(reviewer)}`,
    request
  );
}

/**
 * Add a reviewer comment and/or per-step accept/reject during review.
 * Uses explicit POST so this never hits PATCH .../steps/{stepId} (complete step).
 */
export async function addStepComment(
  runId: string,
  stepId: string,
  request: AddStepCommentRequest
): Promise<ChecklistRun> {
  const rid = encodeURIComponent(runId);
  const sid = encodeURIComponent(stepId);
  const path = `${BASE_PATH}/runs/${rid}/steps/${sid}/comment`;
  return apiRequest<ChecklistRun>({
    method: "POST",
    url: path,
    data: request,
  });
}

/**
 * Have Timmy (AI) review a step response for quality
 */
export async function aiReviewStep(
  request: AIReviewRequest
): Promise<AIReviewResponse> {
  return post<AIReviewResponse>(`${BASE_PATH}/ai-review`, request);
}

/**
 * Upload a screenshot for step evidence
 */
export async function uploadScreenshot(file: File): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append("file", file);
  
  return apiRequest<{ url: string }>({
    method: "POST",
    url: `${BASE_PATH}/upload-screenshot`,
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
}

/**
 * Convert an Azure blob URL to our proxy URL path
 * Returns the API path (not full URL) for use with authenticated fetch
 */
export function getScreenshotProxyPath(azureUrl: string): string {
  // Extract blob path from Azure URL
  // Format: https://{account}.blob.core.windows.net/ops-checklists/{blob_path}
  try {
    const url = new URL(azureUrl);
    const pathParts = url.pathname.split('/');
    // Remove empty string and container name, keep the rest
    const blobPath = pathParts.slice(2).join('/');
    return `${BASE_PATH}/screenshot/${blobPath}`;
  } catch {
    return ""; 
  }
}

/**
 * Fetch a screenshot with authentication and return as blob URL
 * This is needed because img tags can't send auth headers
 */
export async function fetchScreenshotAsBlob(azureUrl: string): Promise<string | null> {
  const path = getScreenshotProxyPath(azureUrl);
  if (!path) return null;
  
  try {
    const response = await apiRequest<Blob>({
      method: "GET",
      url: path,
      responseType: "blob",
    });
    return URL.createObjectURL(response);
  } catch {
    return null;
  }
}

/**
 * Get historical runs with filters
 */
export async function getHistory(filters?: HistoryFilters): Promise<HistoryResponse> {
  const params = new URLSearchParams();
  if (filters?.checklist_id) params.append("checklist_id", filters.checklist_id);
  if (filters?.user_name) params.append("user_name", filters.user_name);
  if (filters?.status) params.append("status", filters.status);
  if (filters?.date_from) params.append("date_from", filters.date_from);
  if (filters?.date_to) params.append("date_to", filters.date_to);
  if (filters?.limit) params.append("limit", String(filters.limit));
  if (filters?.skip) params.append("skip", String(filters.skip));

  const query = params.toString();
  return get<HistoryResponse>(`${BASE_PATH}/history${query ? `?${query}` : ""}`);
}

/**
 * Get all amber/red issues from checklist runs
 */
export async function getIssues(filters?: IssuesFilters): Promise<IssuesResponse> {
  const params = new URLSearchParams();
  if (filters?.date_from) params.append("date_from", filters.date_from);
  if (filters?.date_to) params.append("date_to", filters.date_to);
  if (filters?.assigned_to) params.append("assigned_to", filters.assigned_to);
  if (filters?.status) params.append("status", filters.status);

  const query = params.toString();
  return get<IssuesResponse>(`${BASE_PATH}/issues${query ? `?${query}` : ""}`);
}

/**
 * Get all checklists
 */
export async function getChecklists(
  includeInactive = false
): Promise<Checklist[]> {
  const params = includeInactive ? "?include_inactive=true" : "";
  return get<Checklist[]>(`${BASE_PATH}/checklists${params}`);
}

/**
 * Get a checklist by ID
 */
export async function getChecklist(checklistId: string): Promise<Checklist> {
  return get<Checklist>(`${BASE_PATH}/checklists/${checklistId}`);
}

/**
 * Create a new checklist
 */
export async function createChecklist(
  request: CreateChecklistRequest
): Promise<Checklist> {
  return post<Checklist>(`${BASE_PATH}/checklists`, request);
}

/**
 * Update an existing checklist
 */
export async function updateChecklist(
  checklistId: string,
  request: UpdateChecklistRequest
): Promise<Checklist> {
  return patch<Checklist>(`${BASE_PATH}/checklists/${checklistId}`, request);
}

/**
 * Delete (soft) a checklist
 */
export async function deleteChecklist(
  checklistId: string
): Promise<{ deleted: boolean; checklist_id: string }> {
  return del<{ deleted: boolean; checklist_id: string }>(
    `${BASE_PATH}/checklists/${checklistId}`
  );
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get color for run status
 */
export function getRunStatusColor(status: RunStatus): string {
  switch (status) {
    case "in_progress":
      return "blue";
    case "awaiting_review":
      return "amber";
    case "approved":
      return "green";
    case "rejected":
      return "red";
    default:
      return "slate";
  }
}

/**
 * Get label for run status
 */
export function getRunStatusLabel(status: RunStatus): string {
  switch (status) {
    case "in_progress":
      return "In Progress";
    case "awaiting_review":
      return "Awaiting Review";
    case "approved":
      return "Approved";
    case "rejected":
      return "Rejected";
    default:
      return status;
  }
}

/**
 * Get color for step status (RAG)
 */
export function getStepStatusColor(status: StepStatus): "green" | "amber" | "red" | "zinc" {
  switch (status) {
    case "green":
      return "green";
    case "amber":
      return "amber";
    case "red":
      return "red";
    case "pending":
    default:
      return "zinc";
  }
}

/**
 * Get background color class for step status
 */
export function getStepStatusBgClass(status: StepStatus): string {
  switch (status) {
    case "green":
      return "bg-green-500";
    case "amber":
      return "bg-amber-500";
    case "red":
      return "bg-red-500";
    case "pending":
      return "bg-slate-300 dark:bg-slate-600";
    default:
      return "bg-slate-300 dark:bg-slate-600";
  }
}
